import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit"
import { APIError, AppThunkAPIType, Problem } from "../../app/appTypes"
import { RootState } from "../../app/store"
import { logout, selectBearerToken } from "../user/userSlice"

export type Uuid = string

// All blob traffic now goes through the authenticated vchainer REST API (api/blobs),
// not the standalone "workflow" service. REACT_APP_REST_API ends with a trailing slash.
const blobsUrl = (path: string = ""): string => `${process.env.REACT_APP_REST_API}api/blobs${path}`

// SAS read URLs are minted with a 15-min lifetime by the API; we treat them as good for
// a slightly shorter window to absorb clock skew and in-flight latency before re-minting.
const READ_URL_TTL_MS = 14 * 60 * 1000

/**
 * Turns a failed response into an APIError, keeping the server's ProblemDetails when there is one
 * so the UI can show why it was rejected — the remarks rule reports itself that way.
 *
 * The mutating thunks below need this because fetch only rejects on network failure: without an
 * explicit response.ok check a 400 body would be parsed and stored as if it were the happy path.
 */
const toApiError = async (response: Response): Promise<APIError> => {
    try {
        const problem = await response.json() as Problem
        return { kind: "http", status: response.status, problem }
    } catch {
        return { kind: "http", status: response.status }
    }
}

export interface BlobMeta {
    fileMeta : FileMeta
    blobId   : string
    /** Latest version number of the blob (1-based). */
    version  : number
    /**
     * Position of the file within its document context (1-based, kept dense by the API).
     * Null for files predating the numbering or created outside the blob API.
     */
    sequenceNumber : number | null
    /** When the latest version was uploaded — the content this entry serves. */
    uploadedAt : string | null
    uploadedBy : string | null
    /** Display name for uploadedBy, falling back to the raw id. */
    uploadedByFullName : string | null
}

export interface FileMeta {
    fileName    : string
    contentType : string
}

export interface Blobs {
    resourceId: Uuid
    blobs: BlobMeta[] | null
}

/**
 * Whether attachment changes on a document need a justification. The API enforces this and rejects
 * with 400; screens pass the result to <Attachements requireRemarks> so the field is marked required
 * before the user submits. An unknown status is treated as not requiring one — the server still has
 * the final say.
 */
export const remarksRequiredForDocumentStatus = (status?: string | null): boolean =>
    !!status && status.trim().toUpperCase() !== 'N'

export const loadBlobs = createAsyncThunk<BlobMeta[], Uuid, AppThunkAPIType>(
    "blobs/load",
    async (resourceId, { getState, rejectWithValue }) => {
        try {
            const response = await fetch(blobsUrl(`/${resourceId}`), {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    ...selectBearerToken(getState()),
                }
            })
            return await response.json() as BlobMeta[]
        } catch (error) {
            return rejectWithValue({ kind: 'unknown' })
        }
})

export const loadManyBlobs = createAsyncThunk<Blobs[], Uuid[], AppThunkAPIType>(
    "blobs/loadMany",
    async (resourceIds, { getState, rejectWithValue }) => {
        try {
            const response = await fetch(blobsUrl('/query'), {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    ...selectBearerToken(getState()),
                },
                body: JSON.stringify(resourceIds),
            })
            const responseData = await response.json() as [Uuid, BlobMeta[] | null][]
            return responseData.map(([resId, blobsMeta]): Blobs => {
                const blobs: Blobs = { 
                    resourceId: resId,
                    blobs: blobsMeta ?? [],
                }
                return blobs
            }) 
        } catch (error) {
            return rejectWithValue({ kind: 'unknown' })    
        }
})

export const loadManyBlobsIfEmpty = createAsyncThunk<Blobs[], Uuid[], AppThunkAPIType>(
    "blobs/loadManyIfEmpty",
    async (resourceIds, { getState, dispatch }) => {
        let notLoaded: Uuid[] = []
        let blobs: Blobs[] = []
        for (let i = 0; i < resourceIds.length; i++) {
            const resId = resourceIds[i];
            const storeBlobs = selectBlobsByResourceId(getState(), resId)
            if (storeBlobs === undefined) {
                notLoaded.push(resId)
            } else {
                blobs = blobs.concat(storeBlobs)
            }
        }
        if (notLoaded.length > 0) {
            const downloaded = await dispatch(loadManyBlobs(notLoaded)).unwrap()
            blobs = blobs.concat(downloaded)
        }
        return blobs
    })

/**
 * A child object of a document (a URS requirement or a document reference) that owns at least one
 * attachment, as returned by GET api/document-attachments/{documentGuid}/children. The backend has
 * already resolved a human {@link label} and the {@link blobCount}; the tree only lists children
 * that actually have files.
 */
export interface DocumentAttachmentChild {
    resourceId   : Uuid
    resourceType : string
    label        : string
    blobCount    : number
}

/** Labelled child resources (requirements/references) of a document that have attachments. */
export const loadDocumentAttachmentChildren = createAsyncThunk<DocumentAttachmentChild[], Uuid, AppThunkAPIType>(
    "blobs/loadDocumentAttachmentChildren",
    async (documentGuid, { getState, rejectWithValue }) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_REST_API}api/document-attachments/${documentGuid}/children`,
                {
                    method: 'GET',
                    cache: 'no-cache',
                    headers: {
                        'Content-Type': 'application/json',
                        ...selectBearerToken(getState()),
                    },
                })
            return await response.json() as DocumentAttachmentChild[]
        } catch (error) {
            return rejectWithValue({ kind: 'unknown' })
        }
    })

/** Optional metadata persisted on the uploaded blobs (maps to the backend's
 *  resourceType / parentResourceId / parentResourceType form fields). */
export interface UploadBlobOptions {
    resourceType?: string
    parentResourceId?: string
    parentResourceType?: string
    /** Justification for the change; required by the API once the owning document leaves status NEW. */
    remarks?: string
}

export const uploadBlobs = createAsyncThunk<BlobMeta[], [Uuid, File[], UploadBlobOptions?], AppThunkAPIType>
    ("blobs/upload", async ([resourceId, files, options], { getState, rejectWithValue }) => {
        const fd = new FormData()
        files.forEach(file => {
            fd.append("file", file)
        })
        // Optional object metadata — only sent when provided, so existing callers are unaffected.
        if (options?.resourceType) fd.append("resourceType", options.resourceType)
        if (options?.parentResourceId) fd.append("parentResourceId", options.parentResourceId)
        if (options?.parentResourceType) fd.append("parentResourceType", options.parentResourceType)
        if (options?.remarks) fd.append("remarks", options.remarks)

        try {
            const response = await fetch(blobsUrl(`/${resourceId}`), {
                method: 'POST',
                // No Content-Type header: the browser sets the multipart boundary itself.
                headers: {
                    ...selectBearerToken(getState()),
                },
                body: fd,
            })
            if (!response.ok) {
                return rejectWithValue(await toApiError(response))
            }
            return await response.json() as BlobMeta[]
        } catch (error) {
            console.log(error)
            return rejectWithValue({ kind: 'unknown' })
        }
})

/**
 * Uploads a new version of an existing blob. The backend appends it as the next version
 * (reads then serve the latest) and returns the full, updated blob list for the resource.
 */
export const uploadBlobVersion = createAsyncThunk<BlobMeta[], [Uuid, string, File, string?], AppThunkAPIType>
    ("blobs/uploadVersion", async ([resourceId, blobId, file, remarks], { getState, rejectWithValue }) => {
        const fd = new FormData()
        fd.append("file", file)
        if (remarks) fd.append("remarks", remarks)

        try {
            const response = await fetch(blobsUrl(`/${resourceId}/${blobId}/versions`), {
                method: 'POST',
                // No Content-Type header: the browser sets the multipart boundary itself.
                headers: {
                    ...selectBearerToken(getState()),
                },
                body: fd,
            })
            if (!response.ok) {
                return rejectWithValue(await toApiError(response))
            }
            return await response.json() as BlobMeta[]
        } catch (error) {
            console.log(error)
            return rejectWithValue({ kind: 'unknown' })
        }
})

/** One entry in a blob's version history (read-only). */
export interface BlobVersion {
    version: number
    uploadedAt: string
    uploadedBy: string
    uploadedByFullName: string
    contentType: string
    sizeBytes: number
    /** Why this version was added; null when none was required or given. */
    remarks: string | null
}

/** Loads the full version history (newest first) for a single blob. */
export const loadBlobVersions = createAsyncThunk<BlobVersion[], [Uuid, string], AppThunkAPIType>(
    "blobs/loadVersions",
    async ([resourceId, blobId], { getState, rejectWithValue }) => {
        try {
            const response = await fetch(blobsUrl(`/${resourceId}/${blobId}/versions`), {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    ...selectBearerToken(getState()),
                },
            })
            return await response.json() as BlobVersion[]
        } catch (error) {
            return rejectWithValue({ kind: 'unknown' })
        }
})

/** Mints a signed read URL for a specific version of a blob (caller opens it). */
export const getBlobVersionUrl = createAsyncThunk<string, [Uuid, string, number], AppThunkAPIType>(
    "blobs/versionUrl",
    async ([resourceId, blobId, version], { getState, rejectWithValue }) => {
        try {
            const response = await fetch(blobsUrl(`/${resourceId}/${blobId}/versions/${version}/read-url`), {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    ...selectBearerToken(getState()),
                },
            })
            const data = await response.json() as { url: string }
            return data.url
        } catch (error) {
            return rejectWithValue({ kind: 'unknown' })
        }
})

export const deleteBlob = createAsyncThunk<void, [Uuid, string, string?], AppThunkAPIType>("blobs/delete", async ([resourceId, blobId, remarks], { getState, rejectWithValue }) => {
    // Remarks travel in the query string: DELETE carries no body by convention.
    const query = remarks ? `?remarks=${encodeURIComponent(remarks)}` : ''
    try {
        const response = await fetch(blobsUrl(`/${resourceId}/${blobId}${query}`), {
            method: 'DELETE',
            cache: 'no-cache',
            headers: {
                ...selectBearerToken(getState()),
            },
        })
        if (!response.ok) {
            return rejectWithValue(await toApiError(response))
        }
    } catch (error) {
        return rejectWithValue({ kind: 'unknown' })
    }

})

/**
 * Mints signed (SAS) read URLs for the given blobs, reusing any already cached in the
 * store that are not near expiry, so a screen full of images makes a single request.
 * The resolved URLs land in state.attachements.urls; read them with selectBlobUrl /
 * the <BlobImage> / <BlobLink> wrappers rather than constructing URLs by hand.
 */
export const ensureBlobUrls = createAsyncThunk<Record<string, string>, string[], AppThunkAPIType>(
    "blobs/ensureUrls",
    async (blobIds, { getState, rejectWithValue }) => {
        const { urls } = selectAttachements(getState())
        const now = Date.now()
        const stale = Array.from(new Set(blobIds)).filter(id => {
            const cached = urls[id]
            return !cached || cached.expiresAt <= now
        })

        if (stale.length === 0) {
            return {}
        }

        try {
            const response = await fetch(blobsUrl('/read-urls'), {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    ...selectBearerToken(getState()),
                },
                body: JSON.stringify({ blobIds: stale }),
            })
            return await response.json() as Record<string, string>
        } catch (error) {
            return rejectWithValue({ kind: 'unknown' })
        }
})

const adapter = createEntityAdapter<Blobs>({
    selectId: blob => blob.resourceId
})

export interface CachedReadUrl {
    url: string
    /** Epoch ms after which the SAS URL is considered expired and must be re-minted. */
    expiresAt: number
}

export type BlobsState = EntityState<Blobs> & {
    /** Minted signed read URLs keyed by blobId. */
    urls: Record<string, CachedReadUrl>
}

const initialState: BlobsState = adapter.getInitialState({
    urls: {} as Record<string, CachedReadUrl>,
})

export const attachements = createSlice({
    name: 'attachements',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(loadManyBlobs.fulfilled, (state, action) => {
            adapter.setMany(state, action.payload)
        })
        builder.addCase(loadBlobs.fulfilled, (state, action) => {
            adapter.setOne(state, {
                resourceId: action.meta.arg,
                blobs: action.payload,
            })
        })
        builder.addCase(deleteBlob.fulfilled, (state, action) => {
            const [resourceId, blobId] = action.meta.arg
            const entity = state.entities[resourceId]
            if (entity) {
                const { blobs } = entity
                adapter.updateOne(state, {
                    id: resourceId,
                    changes: {
                        blobs: blobs ? blobs.filter(x => x.blobId !== blobId) : []
                    }
                })
            }
        })
        builder.addCase(uploadBlobs.fulfilled, (state, action) => {
            const [resourceId] = action.meta.arg
            const entity = state.entities[resourceId]
            if (entity) {
                adapter.updateOne(state, {
                    id: resourceId,
                    changes: {
                        blobs: action.payload,
                    }
                })
            } else {
                adapter.setOne(state, {
                    resourceId,
                    blobs: action.payload,
                })
            }
        })
        builder.addCase(uploadBlobVersion.fulfilled, (state, action) => {
            const [resourceId, blobId] = action.meta.arg
            const entity = state.entities[resourceId]
            if (entity) {
                adapter.updateOne(state, {
                    id: resourceId,
                    changes: {
                        blobs: action.payload,
                    }
                })
            } else {
                adapter.setOne(state, {
                    resourceId,
                    blobs: action.payload,
                })
            }
            // The blob's content changed; drop any cached SAS URL so the next render re-mints
            // against the new latest version (the cached URL still points at the old version's path).
            delete state.urls[blobId]
        })
        builder.addCase(ensureBlobUrls.fulfilled, (state, action) => {
            const expiresAt = Date.now() + READ_URL_TTL_MS
            for (const [blobId, url] of Object.entries(action.payload)) {
                state.urls[blobId] = { url, expiresAt }
            }
        })
        builder.addCase(logout.fulfilled, (state, _) => {
            adapter.removeAll(state)
            state.urls = {}
        })
    }
})

export const selectAttachements = (state: RootState) => state.attachements

export const {
    selectAll  : selectAllBlobs,
    selectById : selectBlobsByResourceId,
} = adapter.getSelectors(selectAttachements)

/** Returns a cached signed read URL for a blob if one is present and not yet expired. */
export const selectBlobUrl = (blobId: string | undefined) => (state: RootState): string | undefined => {
    if (!blobId) return undefined
    const cached = state.attachements.urls[blobId]
    if (!cached || cached.expiresAt <= Date.now()) return undefined
    return cached.url
}

/**
 * Extracts a blobId from a value that may either be a bare blobId (the format we persist
 * now) or a legacy absolute blob URL of the form `.../blobs/{resourceId}/{blobId}` that
 * was stored before the signed-URL migration. Returns undefined for null/empty input.
 */
export const blobIdFromStoredRef = (stored: string | null | undefined): string | undefined => {
    if (!stored) return undefined
    if (!stored.includes('/')) return stored
    // Legacy absolute URL: take the last path segment, dropping any query string.
    const lastSegment = stored.split('?')[0].split('/').filter(Boolean).pop()
    return lastSegment || undefined
}

export default attachements.reducer

